import { useEffect, useRef, useState } from 'react';

const useContainerOverflow = ({ deps = [], threshold = 0 }) => {
  const containerRef = useRef(null);
  const contentRefs = useRef([]);
  const [overflowMap, setOverflowMap] = useState({});
  const isCheckingRef = useRef(false);
  const lastStableOverflowRef = useRef({});
  const hasMeasuredRef = useRef(false);
  const [lastOverflowedItemOffsetLeft, setLastOverflowedItemOffsetLeft] =
    useState(0);

  const addContentRef = (index, ref) => {
    if (!hasMeasuredRef.current) {
      contentRefs.current[index] = ref;
    }
  };

  useEffect(() => {
    const container = containerRef.current;

    if (!container || isCheckingRef.current || hasMeasuredRef.current) {
      return;
    }

    const updateOverflowMap = async () => {
      try {
        isCheckingRef.current = true;

        const containerWidth = container.offsetWidth;
        const newOverflowMap = {};

        if (contentRefs.current.length === 0) {
          return;
        }

        let allItemsMeasured = true;

        contentRefs.current.forEach((contentRef, index) => {
          if (!contentRef || contentRef.offsetWidth > 0) {
            newOverflowMap[index] = false;
            allItemsMeasured = false;
            return;
          }

          const contentWidth = contentRef.offsetWidth;
          const containerRight = container.offsetLeft + containerWidth;
          const availableWidth = containerRight - contentRef.offsetLeft;
          const isOverflowed = contentWidth + threshold > availableWidth;

          newOverflowMap[index] = isOverflowed;

          if (isOverflowed && !newOverflowMap[index - 1]) {
            setLastOverflowedItemOffsetLeft(contentRef.offsetLeft);
          }
        });

        if (allItemsMeasured && !hasMeasuredRef.current) {
          setOverflowMap(newOverflowMap);
          lastStableOverflowRef.current = { ...newOverflowMap };
          hasMeasuredRef.current = true;
        }
      } catch (error) {
        const { captureException } = await import('@sentry/nextjs');

        captureException(error, { contexts: { react: error.componentStack } });
      } finally {
        isCheckingRef.current = false;
      }
    };

    updateOverflowMap();
  }, [deps, threshold]);

  return {
    addContentRef,
    containerRef,
    lastOverflowedItemOffsetLeft,
    overflowMap,
  };
};

export default useContainerOverflow;
